import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import axios from 'axios';

const UserProfileContext = createContext();
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error('useUserProfile must be used within UserProfileProvider');
  }
  return context;
};

export const UserProfileProvider = ({ children }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async (user) => {
    if (!user) {
      setUserProfile(null);
      return;
    }

    const token = await user.getIdToken();
    const response = await axios.get(`${API_BASE_URL}/api/get_user_profile/${user.uid}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response.data.success) {
      setUserProfile(response.data.profile);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);

      if (user && user.emailVerified) {
        try {
          await fetchUserProfile(user);
        } catch (error) {
          if (error.response?.status === 404) {
            // No profile exists yet for this authenticated user.
            setUserProfile(null);
          } else {
            console.error('Failed to fetch user profile:', error);
          }
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    userProfile,
    loading,
    setUserProfile,
    refreshUserProfile: async (currentUser = auth.currentUser) => {
      setLoading(true);

      try {
        if (currentUser && currentUser.emailVerified) {
          await fetchUserProfile(currentUser);
        } else {
          setUserProfile(null);
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setUserProfile(null);
        } else {
          console.error('Failed to refresh user profile:', error);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};
